import { addDoc, collection, serverTimestamp, query, where, orderBy, limit, onSnapshot, updateDoc, doc, getDocs, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import notificationAnalytics from './notificationAnalytics';

export type NotificationType = 
  | 'activity_assigned'
  | 'activity_completed'
  | 'checklist_assigned'
  | 'checklist_completed'
  | 'family_invitation'
  | 'member_added'
  | 'member_promoted'
  | 'plan_changed'
  | 'new_reward'            // Nueva recompensa disponible (para miembros)
  | 'reward_requested'      // Canje solicitado (para admin)
  | 'reward_approved'       // Canje aprobado (para miembro)
  | 'reward_rejected'       // Canje rechazado (para miembro)
  | 'badge_earned'          // Ganó un badge de temporada
  | 'new_season'            // Nueva temporada disponible
  | 'season_ending'         // Temporada por terminar
  | 'system_message';

export interface Notification {
  id?: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  icon: string;
  link?: string;
  read: boolean;
  createdAt: any;
  familyId?: string;
  resourceId?: string;
  metadata?: Record<string, any>;
}

class NotificationService {
  private prefsCache = new Map<string, { expiresAt: number; value: any }>();
  private prefsCacheTtlMs = 5 * 60 * 1000;

  private getCachedPrefs(userId: string): any | null {
    const entry = this.prefsCache.get(userId);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.prefsCache.delete(userId);
      return null;
    }
    return entry.value;
  }

  private setCachedPrefs(userId: string, value: any) {
    this.prefsCache.set(userId, { value, expiresAt: Date.now() + this.prefsCacheTtlMs });
  }

  /**
   * Crear una nueva notificación
   */
  async createNotification(
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    options: {
      icon?: string;
      link?: string;
      familyId?: string;
      resourceId?: string;
      metadata?: Record<string, any>;
    } = {}
  ): Promise<string> {
    try {
      // Verificar preferencias del usuario
      const userPrefs = await this.getUserPreferences(userId);
      
      // Si las notificaciones están deshabilitadas o este tipo está deshabilitado, no crear
      // Nota: Si el tipo no existe en las preferencias (indefinido), se asume habilitado por defecto
      if (!userPrefs.enabled || userPrefs.types[type] === false) {
        return '';
      }

      const notification: Notification = {
        userId,
        type,
        title,
        message,
        icon: options.icon || this.getDefaultIcon(type),
        read: false,
        createdAt: serverTimestamp(),
      };

      // Solo agregar campos opcionales si tienen valor
      if (options.link) notification.link = options.link;
      if (options.familyId) notification.familyId = options.familyId;
      if (options.resourceId) notification.resourceId = options.resourceId;
      if (options.metadata) notification.metadata = options.metadata;

      const docRef = await addDoc(collection(db, 'notifications'), notification);
      
      // Track analytics: sent
      await notificationAnalytics.trackEvent(docRef.id, userId, type, 'sent');
      
      // Intentar enviar push notification si está habilitado
      if (userPrefs.pushEnabled) {
        await this.sendPushNotification(userId, title, message, options.link);
      }
      
      // Track analytics: delivered
      await notificationAnalytics.trackEvent(docRef.id, userId, type, 'delivered');
      
      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener preferencias de notificación del usuario
   */
  private async getUserPreferences(userId: string): Promise<any> {
    try {
      const cached = this.getCachedPrefs(userId);
      if (cached) return cached;

      const userDocRef = doc(db, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);
      
      if (!userDocSnap.exists()) {
        const prefs = {
          enabled: true,
          pushEnabled: false,
          types: {
            activity_assigned: true,
            activity_completed: true,
            checklist_assigned: true,
            checklist_completed: true,
            family_invitation: true,
            member_added: true,
            member_promoted: true,
            plan_changed: true,
            reward_requested: true,
            reward_approved: true,
            reward_rejected: true,
            system_message: true,
          },
        };
        this.setCachedPrefs(userId, prefs);
        return prefs;
      }
      
      const userData = userDocSnap.data();
      const prefs = userData.notificationPreferences || {
        enabled: true,
        pushEnabled: false,
        types: {
          activity_assigned: true,
          activity_completed: true,
          checklist_assigned: true,
          checklist_completed: true,
          family_invitation: true,
          member_added: true,
          member_promoted: true,
          plan_changed: true,
          reward_requested: true,
          reward_approved: true,
          reward_rejected: true,
          system_message: true,
        },
      };
      this.setCachedPrefs(userId, prefs);
      return prefs;
    } catch (error) {
      const prefs = {
        enabled: true,
        pushEnabled: false,
        emailEnabled: true,
        types: {
          activity_assigned: true,
          activity_completed: true,
          checklist_assigned: true,
          checklist_completed: true,
          family_invitation: true,
          member_added: true,
          member_promoted: true,
          plan_changed: true,
          reward_requested: true,
          reward_approved: true,
          reward_rejected: true,
          system_message: true,
        },
      };
      this.setCachedPrefs(userId, prefs);
      return prefs;
    }
  }

  /**
   * Obtener icono por defecto según el tipo
   */
  private getDefaultIcon(type: NotificationType): string {
    const icons: Record<NotificationType, string> = {
      activity_assigned: '🎯',
      activity_completed: '✅',
      checklist_assigned: '📋',
      checklist_completed: '✔️',
      family_invitation: '👥',
      member_added: '👤',
      member_promoted: '👑',
      plan_changed: '📊',
      new_reward: '🆕',
      reward_requested: '🎁',
      reward_approved: '🎉',
      reward_rejected: '❌',
      badge_earned: '🏆',
      new_season: '✨',
      season_ending: '⏰',
      system_message: '📢',
    };
    return icons[type] || '🔔';
  }

  /**
   * Marcar notificación como leída
   */
  async markAsRead(notificationId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
      });
    } catch (error) {
    }
  }

  /**
   * Marcar todas las notificaciones de un usuario como leídas
   */
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      
      const snapshot = await getDocs(q);
      const promises = snapshot.docs.map(doc => 
        updateDoc(doc.ref, { read: true })
      );
      
      await Promise.all(promises);
    } catch (error) {
    }
  }

  /**
   * Suscribirse a notificaciones de un usuario
   */
  subscribeToNotifications(
    userId: string,
    callback: (notifications: Notification[]) => void,
    limitCount: number = 50
  ): () => void {
    // Query MÁS SIMPLE sin filtro de fecha
    // Esto debería funcionar siempre
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const notifications: Notification[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          notifications.push({ id: doc.id, ...data } as Notification);
        });
        callback(notifications);
      },
      (error) => {
        console.error('❌ Error loading notifications:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Full error:', JSON.stringify(error, null, 2));

        callback([]);
      }
    );
  }

  /**
   * Enviar push notification (si el usuario tiene token FCM)
   */
  private async sendPushNotification(
    userId: string,
    title: string,
    body: string,
    link?: string
  ): Promise<void> {
    try {
      const userSnap = await getDoc(doc(db, 'users', userId));
      if (!userSnap.exists()) return;
      const userData: any = userSnap.data();
      const fcmToken = userData?.fcmToken;
      
      if (!fcmToken) return;

      // En producción, esto debería hacerse desde Cloud Functions
      // Por ahora, usamos la API de FCM directamente desde el cliente
      // Nota: Esto requiere configurar CORS en Firebase
      
      
      // TODO: Implementar Cloud Function para enviar push
      // await fetch('https://fcm.googleapis.com/fcm/send', { ... });
      
    } catch (error) {
    }
  }

  /**
   * Helpers para crear notificaciones específicas
   */

  async notifyActivityAssigned(
    userId: string,
    activityName: string,
    familyId: string,
    activityId: string,
    memberId?: string
  ): Promise<void> {
    await this.createNotification(
      userId,
      'activity_assigned',
      'Nueva actividad asignada',
      `Te han asignado: ${activityName}`,
      {
        link: `/groups/board`,
        familyId,
        resourceId: activityId,
        metadata: memberId ? { memberId, activityId } : { activityId },
      }
    );
  }

  async notifyActivityCompleted(
    userId: string,
    activityName: string,
    completedBy: string,
    familyId: string
  ): Promise<void> {
    await this.createNotification(
      userId,
      'activity_completed',
      'Actividad completada',
      `${completedBy} completó: ${activityName}`,
      {
        familyId,
      }
    );
  }

  async notifyChecklistAssigned(
    userId: string,
    checklistName: string,
    familyId: string,
    checklistId: string,
    memberId?: string
  ): Promise<void> {
    await this.createNotification(
      userId,
      'checklist_assigned',
      'Nueva tarea asignada',
      `Te han asignado: ${checklistName}`,
      {
        link: `/groups/board`,
        familyId,
        resourceId: checklistId,
        metadata: memberId ? { memberId, checklistId } : { checklistId },
      }
    );
  }

  async notifyChecklistCompleted(
    userId: string,
    checklistName: string,
    completedBy: string,
    familyId: string
  ): Promise<void> {
    await this.createNotification(
      userId,
      'checklist_completed',
      'Tarea completada',
      `${completedBy} completó: ${checklistName}`,
      {
        familyId,
      }
    );
  }

  async notifyFamilyInvitation(
    userId: string,
    familyName: string,
    invitedBy: string,
    familyId: string
  ): Promise<void> {
    await this.createNotification(
      userId,
      'family_invitation',
      'Nueva invitación',
      `${invitedBy} te invitó a unirte a ${familyName}`,
      {
        link: '/groups/dashboard', // Dashboard muestra el componente FamilyInvitations
        familyId,
      }
    );
  }

  async notifyMemberAdded(
userId: string, memberName: string, familyId: string, familyName: any  ): Promise<void> {
    await this.createNotification(
      userId,
      'member_added',
      'Nuevo miembro',
      `${memberName} se unió al grupo`,
      {
        familyId,
      }
    );
  }

  async notifyMemberPromoted(
    userId: string,
    memberName: string,
    familyId: string
  ): Promise<void> {
    await this.createNotification(
      userId,
      'member_promoted',
      'Nuevo administrador',
      `${memberName} ahora es administrador`,
      {
        familyId,
      }
    );
  }

  async notifyPlanChanged(
    userId: string,
    newPlan: string,
    familyId: string
  ): Promise<void> {
    await this.createNotification(
      userId,
      'plan_changed',
      'Plan actualizado',
      `Tu plan ha cambiado a: ${newPlan}`,
      {
        link: '/subscription',
        familyId,
      }
    );
  }

  async notifySystemMessage(
    userId: string,
    title: string,
    message: string,
    link?: string
  ): Promise<void> {
    await this.createNotification(
      userId,
      'system_message',
      title,
      message,
      {
        link,
      }
    );
  }
}

// Instancia singleton
const notificationService = new NotificationService();

export default notificationService;
